import { FolderType } from "@/types/productTypes";
import { ChevronRight, FolderIcon } from "lucide-react";
import { useState } from "react";
import { useDrop } from "react-dnd";
import { getFolderImageCount } from "../../../../utils/folderUtils";
import { useAdminStore } from "../../../../store/useAdminStore";

type FolderItemProps = {
   folder: FolderType;
   onDrop: (folderId: number, imageId: number) => void;
   selected: boolean;
   onSelect: (folder: FolderType) => void;
   level?: number;
};

export default function FolderItem({
   folder,
   onDrop,
   selected,
   onSelect,
   level = 0,
}: FolderItemProps) {
   const [isOpen, setIsOpen] = useState(false);
   const hasChildren = folder.children && folder.children.length > 0;
   const { assignImageToFolder, allImages, setOptimisticImages } =
      useAdminStore();
   const imageCount = getFolderImageCount(folder, allImages);

   const [{ isOver, canDrop }, drop] = useDrop(
      () => ({
         accept: "IMAGE",
         drop: async (item: { id: number }) => {
            // Optimistically update the UI
            const optimisticImages = allImages.map((img) =>
               img.id === item.id ? { ...img, folderId: folder.id } : img
            );
            setOptimisticImages(optimisticImages);

            try {
               await assignImageToFolder(item.id, folder.id);
            } catch (error) {
               console.error("Error moving image:", error);
               // Revert the optimistic update on error
               const revertedImages = allImages.map((img) =>
                  img.id === item.id ? { ...img, folderId: img.folderId } : img
               );
               setOptimisticImages(revertedImages);
            }
            return { folderId: folder.id };
         },
         collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
         }),
      }),
      [folder, allImages]
   );

   const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(folder);
   };

   const handleToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsOpen(!isOpen);
   };

   // Updated isSelected function with correct type checking
   const isSelected = (currentFolder: FolderType): boolean => {
      if (selected) return true;
      if (currentFolder.children && currentFolder.children.length > 0) {
         return currentFolder.children.some((child) => isSelected(child));
      }
      return false;
   };

   return (
      <div ref={drop} className={`${isSelected(folder) ? "bg-blue-50" : ""}`}>
         <div
            className={`
                    flex items-center p-2 rounded-md cursor-pointer
                    ${selected ? "bg-blue-100" : "hover:bg-gray-100"}
                    ${isOver && canDrop ? "border-2 border-blue-200" : ""}
                    ${level > 0 ? "ml-4" : ""}
                `}
         >
            {hasChildren && (
               <div
                  onClick={handleToggle}
                  className="p-1 hover:bg-gray-200 rounded-sm mr-1"
               >
                  <ChevronRight
                     className={`h-4 w-4 transition-transform ${
                        isOpen ? "transform rotate-90" : ""
                     }`}
                  />
               </div>
            )}
            {!hasChildren && <div className="w-6" />}
            <div className="flex items-center flex-1" onClick={handleClick}>
               <FolderIcon className="h-4 w-4 mr-2" />
               <span className="text-sm">{folder.name}</span>
               <span className="ml-auto text-xs text-gray-500">
                  {imageCount}
               </span>
            </div>
         </div>
         {hasChildren && isOpen && (
            <div className="ml-4">
               {folder.children?.map((child) => (
                  <FolderItem
                     key={child.id}
                     folder={child}
                     onDrop={onDrop}
                     selected={selected}
                     onSelect={onSelect}
                     level={level + 1}
                  />
               ))}
            </div>
         )}
      </div>
   );
}
